import '~/map/page/matchup/matchupData.container.autoload';
import {
  type FC, useCallback, useMemo,
} from 'react';
import { useDispatch } from 'react-redux';
import {
  matchUpDataAllDataSections, matchupDataOnlyInfoPanelSection,
} from '~/_shared/components/matchUpDataModal/matchUpDataModal.component';
import { MapTool } from '~/_shared/types/toolsAndFeatures/mapTools.types';
import { useIntercom } from '~/_shared/utils/3rdPartyIntegrations/intercom/intercom.context';
import { useTranslation } from '~/_shared/utils/hooks';
import { useSelector } from '~/_shared/utils/hooks/useSelector';
import { openUrlInNewTab } from '~/_shared/utils/link/link';
import { useLogOutUser } from '~/authentication/logOutUser.hook';
import { ExportDataSection } from '~/data/exportDataModal/exportDataSection.enum';
import { useSyncGoogleSheet } from '~/data/syncData/useSyncGoogleSheet.hook';
import { useMainMenuHighlightedItems } from '~/mainMenu/useMainMenuHighlightedItems';
import { getRelativeMapPresentationalUrl } from '~/map/map.helpers';
import { useModal } from '~/modal/useModal.hook';
import { useIsMobileScreenSelector } from '~/store/frontendState/deviceInfo/deviceInfo.selector';
import {
  openMapTool, openMapToolExclusively,
} from '~/store/frontendState/leftSidebar/leftSidebar.actionCreators';
import { useIsPresentationalMenuShownSelector } from '~/store/frontendState/mapPresentational/mapPresentational.selectors';
import { useSpreadsheetTableSelector } from '~/store/frontendState/spreadsheetTable/spreadsheetTable.selectors';
import { useSpreadsheetTableRowsDelete } from '~/store/frontendState/spreadsheetTable/useSpreadsheetTableRowsDelete';
import { checkIsSalesforceMap } from '~/store/mapInfo/mapInfo.helpers';
import {
  useIsConnectedLayeredMapSelector, useMapInfoDataSelector,
} from '~/store/mapInfo/mapInfo.selectors';
import { useClientIdSelector } from '~/store/selectors/useClientIdSelector';
import { useMapIdSelector } from '~/store/selectors/useMapIdSelector';
import { useIsMapPresentationalSelector } from '~/store/selectors/useMapInfoSelectors';
import { useCreateRowExternal } from '~/store/spreadsheetData/externalSources/useCreateRowExternal';
import { useSplitPanelState } from '~/store/userData/settings/splitPanel/useSplitPanelState';
import {
  userSettingsSplitPanelClose, userSettingsSplitPanelOpen,
} from '~/store/userData/settings/userDataSettings.actionCreators';
import { MainMenuComponent } from './mainMenu.component';
import {
  type MainMenuItem, MainMenuItems,
} from './mainMenuItem.enum';

type MainMenuContainer = Readonly<{
  menuHeight: number;
  selectedItem: MainMenuItem | null;
  visibleMenuItems: ReadonlySet<MainMenuItem>;

  onItemClick: (newApp: MainMenuItem) => void;
}>;

export const MainMenuContainer: FC<MainMenuContainer> = (props) => {
  const [t] = useTranslation();
  const dispatch = useDispatch();
  const mapInfo = useMapInfoDataSelector();
  const isMapInfoLoading = useSelector(s => s.map.mapInfo.isLoading || typeof s.map.mapInfo.data?.id !== 'number');
  const isPresentationalMenuOpen = useIsPresentationalMenuShownSelector();
  const isLayeredMapConnected = useIsConnectedLayeredMapSelector();
  const { openModal: openImportDataModal } = useModal('ImportData');
  const { openModal: openMatchLocationModal } = useModal('MatchupData');
  const { openModal: openAddNewDataColumnModal } = useModal('AddDataColumn');
  const { openModal: openAddNewDataRowModal } = useModal('AddMultipleLocations');
  const { openModal: openExportDataModal } = useModal('ExportSpreadsheetData');
  const syncGoogleSheet = useSyncGoogleSheet();
  const spreadsheetTableState = useSpreadsheetTableSelector();
  const isMobileScreen = useIsMobileScreenSelector();
  const isViewPresentational = useIsMapPresentationalSelector();
  const logOutUser = useLogOutUser();
  const deleteRows = useSpreadsheetTableRowsDelete();
  const clientId = useClientIdSelector();
  const mapId = useMapIdSelector();
  const { isActive: isSplitScreenActive } = useSplitPanelState(mapId);
  const { show: showIntercom } = useIntercom();
  const { highlightedMenuItems } = useMainMenuHighlightedItems();
  const { create: createRowExternal, isSalesforce: isCreateRowSalesforce } = useCreateRowExternal();

  const mapShareId = mapInfo?.shareId;
  const isSalesforceMap = checkIsSalesforceMap(mapInfo);

  const { onItemClick } = props;

  const onSplitClick = useCallback(() => {
    if (clientId === null || mapId === null) {
      return;
    }

    if (isSplitScreenActive) {
      dispatch(userSettingsSplitPanelClose(clientId, mapId));
    }
    else {
      dispatch(userSettingsSplitPanelOpen(clientId, mapId));
    }
  }, [dispatch, clientId, isSplitScreenActive, mapId]);

  const openExportSpreadsheetDataModal = useCallback(() => {
    openExportDataModal({
      availableExportSections: [ExportDataSection.All, ExportDataSection.FilteredTableSearch],
    });
  }, [openExportDataModal]);

  const onAddNewDataRow = useCallback(() => {
    if (isCreateRowSalesforce) {
      createRowExternal?.();
    }
    else {
      openAddNewDataRowModal();
    }
  }, [createRowExternal, isCreateRowSalesforce, openAddNewDataRowModal]);

  const additionalOnClickHandlers: Partial<{ [key in MainMenuItem]: () => void }> = useMemo(() => ({
    SplitScreen: onSplitClick,
    LogOut: logOutUser,
    Present: () => mapShareId && openUrlInNewTab(getRelativeMapPresentationalUrl(mapShareId)),
    FilterData: () => dispatch(openMapTool(MapTool.FilterData)),
    ImportNewData: () => openImportDataModal({}),
    MatchMyLocationData: () => openMatchLocationModal({
      ensureLocationDataIsSelected: false,
      sections: isLayeredMapConnected ? matchupDataOnlyInfoPanelSection : matchUpDataAllDataSections,
    }),
    AddNewDataColumn: openAddNewDataColumnModal,
    AddNewDataRow: onAddNewDataRow,
    ExportData: openExportSpreadsheetDataModal,
    SyncData: () => syncGoogleSheet({}),
    RemoveDataRow: deleteRows,
    Boundary: () => dispatch(openMapToolExclusively(MapTool.Boundary)),
    Proximity: () => dispatch(openMapToolExclusively(MapTool.Proximity)),
    Filter: () => dispatch(openMapToolExclusively(MapTool.Filter)),
    Grouping: () => dispatch(openMapToolExclusively(MapTool.Grouping)),
    HeatMap: () => dispatch(openMapToolExclusively(MapTool.HeatMap)),
    MapLegend: () => dispatch(openMapToolExclusively(MapTool.MapLegend)),
    Routing: () => dispatch(openMapToolExclusively(MapTool.Routing)),
    Places: () => dispatch(openMapToolExclusively(MapTool.Places)),
    DataLayers: () => dispatch(openMapToolExclusively(MapTool.DataLayers)),
  }), [logOutUser, openAddNewDataColumnModal, onAddNewDataRow, openExportSpreadsheetDataModal, deleteRows,
    dispatch, mapShareId, openImportDataModal, openMatchLocationModal, syncGoogleSheet, onSplitClick,
    isLayeredMapConnected,
  ]);

  const additionalOnItemClick = useCallback((item: MainMenuItem): void => {
    onItemClick(item);
    additionalOnClickHandlers[item]?.();
  }, [additionalOnClickHandlers, onItemClick]);

  const additionalSelectedItems: ReadonlyArray<MainMenuItem> = useMemo(() => ([
    [MainMenuItems.SplitScreen, isSplitScreenActive],
  ] as const)
    .filter(([, isSelected]) => isSelected)
    .map(([item]) => item)
  , [isSplitScreenActive]);

  const selectedItems = useMemo(() =>
    new Set(props.selectedItem ? [props.selectedItem, ...additionalSelectedItems] : additionalSelectedItems),
  [additionalSelectedItems, props.selectedItem]);

  const disabledItems = useMemo(() => {
    const items = new Set<MainMenuItem>();

    if (isMapInfoLoading) {
      items.add(MainMenuItems.ShareMap);
    }

    if (spreadsheetTableState.isRemoveInProgress || Object.keys(spreadsheetTableState.selectedRows).length === 0) {
      items.add(MainMenuItems.RemoveDataRow);
    }

    if (isSalesforceMap) {
      items.add(MainMenuItems.AddNewDataColumn);
      items.add(MainMenuItems.RemoveDataRow);
    }

    return items;
  }, [isMapInfoLoading, isSalesforceMap, spreadsheetTableState.isRemoveInProgress, spreadsheetTableState.selectedRows]);

  const itemTooltips = useMemo(() => {
    const items = new Map<MainMenuItem, string>();

    if (isSalesforceMap) {
      items.set(MainMenuItems.AddNewDataColumn, t('salesforce.spreadsheetData.cannotEditInApp'));
      items.set(MainMenuItems.AddNewDataRow, t('salesforce.spreadsheetData.add.inSalesforce'));
      items.set(MainMenuItems.RemoveDataRow, t('salesforce.spreadsheetData.cannotEditInApp'));
    }

    return items;
  }, [isSalesforceMap, t]);

  const dangerItems = new Set<MainMenuItem>([MainMenuItems.RemoveDataRow]);

  if (isMobileScreen) {
    return null;
  }

  return (
    <MainMenuComponent
      dangerMenuItems={dangerItems}
      disabledItems={disabledItems}
      itemTooltips={itemTooltips}
      visibleMenuItems={props.visibleMenuItems}
      highlightedMenuItems={highlightedMenuItems}
      isPresentationalMenuOpen={isPresentationalMenuOpen}
      isViewPresentational={isViewPresentational}
      menuHeight={props.menuHeight}
      onItemClick={additionalOnItemClick}
      selectedItems={selectedItems}
      openChat={showIntercom}
    />
  );
};
